import React, { useState, useCallback, useMemo } from 'react';
import { HeartPulse, Plus, X, Pencil, Trash2, Download, ClipboardList, Target, Printer } from 'lucide-react';
import ToolHeader from '../shared/ToolHeader';
import { Section, Card, IconTile } from '../hospital/shared/primitives';
import { LinkRow, NumberedRows } from '../shared/ListRows';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useBackHandler } from '../../hooks/NavigationContext';
import { notify } from '../../utils/notify';

/*
 * Basic TEE Certification: the NBE supervised training pathway with live counts, and the case
 * log that documents it. Entries live on this device (localStorage) until user accounts exist;
 * the CSV export matches the NBE sample case log columns.
 *
 * Source: NBE Adult Basic PTE Certification application, 2026 (echoboards.org).
 */

const ACCENT = '#111827';
const NBE_APPLICATION = 'https://www.echoboards.org/wp-content/uploads/2022/11/BasicPTE_Cert_App-2026.pdf';
const NBE_HOME = 'https://www.echoboards.org';
const ASE_BASIC_EXAM = 'https://www.asecho.org/wp-content/uploads/2013/05/Basic-Perioperative-TEE-Exam.pdf';
const STORAGE_KEY = 'tee-case-log';

const TARGET_TOTAL = 150;
const TARGET_PERFORMED = 50;

const SITES = ["Baylor St. Luke's / THI", 'Ben Taub', 'Texas Children\'s', 'VA Medical Center', 'MD Anderson', 'Other'];

const REQUIREMENTS = [
  ['Start logging on the first cardiac rotation', 'The 150 examinations take time. The examination can come later.'],
  ['150 examinations under supervision', 'Within four years, during residency.'],
  ['At least 50 personally performed and interpreted', 'Attending present for the critical parts.'],
  ['Perioperative, adults only', 'Surgery in the same admission. Cardioversion and ablation studies do not count.'],
  ['Pass the Basic PTEeXAM', 'Any time. Certification dates from the pass.'],
  ['Apply once board certified in anesthesiology', 'Licence, plus a notarized letter from the program director with the exact numbers.'],
];

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ id: '', date: today(), site: SITES[0], performed: true, diagnosis: '', surgery: '', attending: '', notes: '' });
const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));

const fmtDate = (iso) => { const d = new Date(`${iso}T12:00:00`); return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };

const Progress = ({ label, value, target }) => {
  const pct = Math.min(100, Math.round((value / target) * 100));
  return (
    <div className="px-4 py-3">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="font-medium text-[15px] text-gray-900 dark:text-white">{label}</div>
        <div className="text-sm tabular-nums text-gray-600 dark:text-gray-300"><span className="font-semibold text-gray-900 dark:text-white">{value}</span> of {target}</div>
      </div>
      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: ACCENT }} /></div>
    </div>
  );
};

const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const exportCsv = async (entries) => {
  const sorted = [...entries].sort((a, b) => (a.date < b.date ? -1 : 1));
  const rows = [['Number', 'Date', 'Site', 'Performed or studied', 'Diagnosis', 'Surgery performed', 'With whom findings discussed', 'Notes']];
  sorted.forEach((e, i) => rows.push([i + 1, e.date, e.site, e.performed ? 'Performed and interpreted' : 'Studied, not performed', e.diagnosis, e.surgery, e.attending, e.notes]));
  const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const name = `tee-case-log-${today()}.csv`;
  try {
    const file = new File([blob], name, { type: 'text/csv' });
    if (typeof navigator.share === 'function' && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: 'TEE case log' });
      return;
    }
  } catch (e) { if (e && e.name === 'AbortError') return; }
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove();
};

const input = 'w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:border-gray-900 dark:focus:border-gray-300 focus:outline-none';

/** The log as the NBE sample case log lays it out; visible only when printing (print-root in index.css) */
const PrintLog = ({ entries }) => {
  const byDate = (a, b) => (a.date < b.date ? -1 : 1);
  const performed = entries.filter((e) => e.performed).sort(byDate);
  const studied = entries.filter((e) => !e.performed).sort(byDate);
  const Table = ({ title, rows, note }) => (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>{title}</h2>
      {note && <p style={{ fontSize: 11, margin: '0 0 8px', color: '#444' }}>{note}</p>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead><tr>{['Number', 'Date', 'Diagnosis', 'Surgery performed', 'With whom findings discussed', 'Site'].map((h) => <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #000', padding: '4px 6px' }}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((e, i) => <tr key={e.id}>{[i + 1, fmtDate(e.date), e.diagnosis, e.surgery, e.attending, e.site].map((v, j) => <td key={j} style={{ borderBottom: '1px solid #ccc', padding: '4px 6px', verticalAlign: 'top' }}>{v}</td>)}</tr>)}</tbody>
      </table>
      {rows.length === 0 && <p style={{ fontSize: 11, color: '#666' }}>None logged.</p>}
    </section>
  );
  return (
    <div id="print-root" className="hidden print:block" style={{ color: '#000', fontFamily: 'Helvetica, Arial, sans-serif', padding: 24 }}>
      <h1 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 2px' }}>Basic Perioperative TEE Case Log</h1>
      <p style={{ fontSize: 11, margin: '0 0 14px', color: '#444' }}>Specific training in basic perioperative TEE (NBE Requirement 4, supervised training pathway). Exported {fmtDate(today())} from the BCM Anesthesiology App.</p>
      <p style={{ fontSize: 12, margin: '0 0 4px' }}>Applicant's full name: ____________________________________</p>
      <p style={{ fontSize: 12, margin: '0 0 18px' }}>Applicant's date of birth: ____________________________________</p>
      <p style={{ fontSize: 12, margin: '0 0 18px' }}>Examinations studied: <strong>{entries.length}</strong> · personally performed and interpreted: <strong>{performed.length}</strong> · studied but not performed: <strong>{studied.length}</strong></p>
      <Table title="Examinations personally performed and interpreted" rows={performed} note="The supervising physician was present for all critical aspects of the procedure." />
      <Table title="Examinations studied but NOT performed" rows={studied} note="Reviewed under supervision; findings discussed with the faculty named." />
    </div>
  );
};

const TeeCertificationScreen = ({ onBack, backLabel = 'Clinical Tools' }) => {
  const [entries, setEntries] = useLocalStorage(STORAGE_KEY, []);
  const [sheet, setSheet] = useState(null); // null | entry being edited (id '' for new)
  const [view, setView] = useState('requirements');
  const close = useCallback(() => setSheet(null), []);
  useBackHandler(Boolean(sheet), close);

  const performed = entries.filter((e) => e.performed).length;
  const total = entries.length;
  const attendings = useMemo(() => [...new Set(entries.map((e) => e.attending).filter(Boolean))].sort(), [entries]);
  const sorted = useMemo(() => [...entries].sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0)), [entries]);

  const save = () => {
    if (!sheet.date || !sheet.diagnosis.trim() || !sheet.surgery.trim() || !sheet.attending.trim()) { notify.warn('Date, diagnosis, surgery and attending are needed.'); return; }
    const clean = { ...sheet, diagnosis: sheet.diagnosis.trim(), surgery: sheet.surgery.trim(), attending: sheet.attending.trim(), notes: (sheet.notes || '').trim() };
    if (clean.id) setEntries((list) => list.map((e) => (e.id === clean.id ? clean : e)));
    else setEntries((list) => [...list, { ...clean, id: newId() }]);
    setSheet(null);
  };
  const remove = (id) => { setEntries((list) => list.filter((e) => e.id !== id)); setSheet(null); };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ToolHeader title="Basic TEE Certification" subtitle="NBE supervised training pathway" icon={HeartPulse} accent={ACCENT} backLabel={backLabel} onBack={onBack} />
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-200/70 dark:bg-gray-800" role="tablist">
          {[['requirements', 'Requirements'], ['log', 'Case log']].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)} className={`py-2 rounded-lg text-sm font-semibold transition-colors ${view === id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>{label}</button>
          ))}
        </div>

        <Section title="Your progress">
          <Card>
            <Progress label="Examinations studied" value={total} target={TARGET_TOTAL} />
            <Progress label="Personally performed and interpreted" value={performed} target={TARGET_PERFORMED} />
            {view === 'log' && <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">Entries are kept on this device. Export the log before changing phones.</div>}
          </Card>
        </Section>

        {view === 'requirements' && (
        <Section title="Requirements">
          <Card>
            <div className="px-4 py-3 flex items-center gap-3"><IconTile icon={Target} accent={ACCENT} /><div className="text-sm text-gray-600 dark:text-gray-400">NBE supervised training pathway.</div></div>
            <NumberedRows items={REQUIREMENTS} accent={ACCENT} />
          </Card>
        </Section>
        )}

        {view === 'requirements' && (
        <Section title="Resources">
          <Card>
            <LinkRow title="Basic Perioperative TEE Examination" subtitle="ASE and SCA guidelines, the views and their sequence (PDF)" href={ASE_BASIC_EXAM} />
            <LinkRow title="NBE application and sample case log" subtitle="Basic PTE certification, 2026 (PDF)" href={NBE_APPLICATION} />
            <LinkRow title="echoboards.org" subtitle="Examination dates and registration" href={NBE_HOME} />
          </Card>
        </Section>
        )}

        {view === 'log' && (
        <Section title={`Case log · ${total}`} aside={total > 0 && (
          <span className="inline-flex items-center gap-3">
            <button type="button" onClick={() => exportCsv(entries)} className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: ACCENT }}><Download size={13} /> CSV</button>
            <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: ACCENT }}><Printer size={13} /> PDF</button>
          </span>
        )}>
          <button type="button" onClick={() => setSheet(blank())} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-semibold transition-colors mb-3" style={{ backgroundColor: ACCENT }}><Plus size={18} /> Log an examination</button>
          <Card>
            {sorted.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"><ClipboardList size={24} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />No examinations logged yet.</div>
            )}
            {sorted.map((e) => (
              <button key={e.id} type="button" onClick={() => setSheet({ ...e })} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[15px] text-gray-900 dark:text-white truncate">{e.surgery}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{e.diagnosis} · {e.attending}</div>
                  <div className="text-xs mt-0.5"><span className={e.performed ? 'font-medium' : 'text-gray-400 dark:text-gray-500'} style={e.performed ? { color: ACCENT } : undefined}>{e.performed ? 'Performed' : 'Studied'}</span><span className="text-gray-400 dark:text-gray-500"> · {fmtDate(e.date)} · {e.site}</span></div>
                </div>
                <Pencil size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
              </button>
            ))}
          </Card>
        </Section>
        )}
      </div>
      <div className="h-8" />

      <PrintLog entries={entries} />

      {sheet && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn" onClick={() => setSheet(null)}>
          <div role="dialog" aria-label="Log an examination" onClick={(ev) => ev.stopPropagation()} className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h2 className="font-display text-lg font-semibold text-gray-900 dark:text-white">{sheet.id ? 'Edit examination' : 'Log an examination'}</h2>
              <button onClick={() => setSheet(null)} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} /></button>
            </div>
            <div className="px-5 pt-2 pb-4 space-y-4">
              <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-900" role="tablist">
                {[[true, 'Performed'], [false, 'Studied']].map(([v, label]) => (
                  <button key={label} type="button" role="tab" aria-selected={sheet.performed === v} onClick={() => setSheet({ ...sheet, performed: v })} className={`py-2 rounded-lg text-sm font-semibold transition-colors ${sheet.performed === v ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>{label}</button>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">{sheet.performed ? 'You performed and interpreted the examination with the attending present.' : 'You reviewed an examination performed by someone else and discussed the findings.'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="eyebrow mb-1.5 block">Date</label><input type="date" value={sheet.date} max={today()} onChange={(e) => setSheet({ ...sheet, date: e.target.value })} className={input} /></div>
                <div><label className="eyebrow mb-1.5 block">Site</label><select value={sheet.site} onChange={(e) => setSheet({ ...sheet, site: e.target.value })} className={input}>{SITES.map((s) => <option key={s}>{s}</option>)}</select></div>
              </div>
              <div><label className="eyebrow mb-1.5 block">Diagnosis</label><input value={sheet.diagnosis} onChange={(e) => setSheet({ ...sheet, diagnosis: e.target.value })} placeholder="e.g. Severe aortic stenosis" className={input} /></div>
              <div><label className="eyebrow mb-1.5 block">Surgery performed</label><input value={sheet.surgery} onChange={(e) => setSheet({ ...sheet, surgery: e.target.value })} placeholder="e.g. Aortic valve replacement" className={input} /></div>
              <div>
                <label className="eyebrow mb-1.5 block">Attending with whom findings were discussed</label>
                <input list="tee-attendings" value={sheet.attending} onChange={(e) => setSheet({ ...sheet, attending: e.target.value })} placeholder="Full name" className={input} autoComplete="off" />
                <datalist id="tee-attendings">{attendings.map((a) => <option key={a} value={a} />)}</datalist>
              </div>
              <div><label className="eyebrow mb-1.5 block">Notes</label><input value={sheet.notes || ''} onChange={(e) => setSheet({ ...sheet, notes: e.target.value })} placeholder="Optional" className={input} /></div>
              <div className="flex gap-3 pt-1">
                {sheet.id && <button type="button" onClick={() => remove(sheet.id)} className="px-4 py-3 rounded-xl text-sm font-semibold text-red-700 border-2 border-gray-200 dark:border-gray-600 inline-flex items-center gap-1.5"><Trash2 size={15} /> Delete</button>}
                <button type="button" onClick={save} className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeeCertificationScreen;
